/**
 * Email Layout Controller — thin adapter that delegates to EmailLayoutManager.
 *
 * The heavy lifting (ResizeObserver, CSS var, signal effects) lives in
 * EmailLayoutManager, which is initialized by the root email_controller.js.
 *
 * This controller remains in place because the ERB views reference
 * data-controller="email-layout" for action handlers (toggleSidebar, showList,
 * showPane, startResize). It reads the store from window._emailStore and
 * forwards actions there.
 */

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['sidebar', 'list', 'readingPane', 'resizer']
  static values  = { sidebarCollapsed: Boolean }

  connect() {
    // EmailLayoutManager (via email_controller) already set up effects.
    // Restore layout state here in case this controller connects before
    // email_controller (unlikely but safe).
    const store = this._store()
    if (!store) {
      // Fallback: use localStorage directly
      const collapsed = localStorage.getItem('email_sidebar_collapsed') === 'true'
      this.sidebarCollapsedValue = collapsed
      this._applyLayout()
      const savedWidth = localStorage.getItem('email_list_width')
      if (savedWidth) this.element.style.setProperty('--email-list-width', savedWidth)
      if (window.innerWidth < 768) this.element.classList.add('email-layout--show-list')
    }
  }

  // ── Actions (called from ERB data-action attributes) ──────────────────────

  toggleSidebar() {
    const store = this._store()
    if (store) {
      store.toggleSidebar()
    } else {
      this.sidebarCollapsedValue = !this.sidebarCollapsedValue
      this._applyLayout()
      localStorage.setItem('email_sidebar_collapsed', this.sidebarCollapsedValue)
    }
  }

  showList() {
    const store = this._store()
    if (store) {
      store.showListPanel()
    } else {
      if (window.innerWidth >= 768) return
      this.element.classList.add('email-layout--show-list')
      this.element.classList.remove('email-layout--show-pane')
    }
  }

  showPane() {
    const store = this._store()
    if (store) {
      store.showReadingPane()
    } else {
      if (window.innerWidth >= 768) return
      this.element.classList.add('email-layout--show-pane')
      this.element.classList.remove('email-layout--show-list')
    }
  }

  startResize(e) {
    // EmailLayoutManager handles this via the data-email-resizer attribute;
    // this action binding is kept for backward compat with ERB views.
    // The layout manager picks up the same mousedown event via its own listener.
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _applyLayout() {
    this.element.classList.toggle('email-layout--sidebar-collapsed', this.sidebarCollapsedValue)
  }

  _store() {
    return window._emailStore || null
  }
}
